<app-modal
  title="Configure Data Element"
  subtitle="Define field properties and data types"
  (closed)="closed.emit()"
>
  @if (loading()) {
    <div class="flex items-center justify-center py-10 text-xs text-slate-400">Loading...</div>
  } @else {
    <!-- Element Name -->
    <div class="flex flex-col gap-1.5 mb-5">
      <label class="text-xs font-semibold text-navy-600 tracking-wider">Element Name</label>
      <input
        type="text"
        placeholder="Type name..."
        [value]="name()"
        (input)="name.set($any($event.target).value)"
        [class.border-red-400]="showErrors() && !name().trim()"
        [class.ring-2]="showErrors() && !name().trim()"
        [class.ring-red-100]="showErrors() && !name().trim()"
        class="w-full"
      />
      @if (showErrors() && !name().trim()) {
        <span class="text-2xs text-red-500">Required</span>
      }
    </div>

    <!-- Data Type -->
    <div class="flex flex-col gap-1.5 mb-5">
      <label class="text-xs font-semibold text-navy-600 tracking-wider">Data Type</label>
      <app-select
        placeholder="Select data type"
        [options]="dataTypeOptions()"
        [value]="dataTypeId()"
        [error]="showErrors() && !dataTypeId()"
        (changed)="onDataTypeChanged($event)"
      />
      @if (showErrors() && !dataTypeId()) {
        <span class="text-2xs text-red-500">Required</span>
      }
    </div>

    <!-- Definition -->
    <div class="flex flex-col gap-1.5 mb-5">
      <label class="text-xs font-semibold text-navy-600 tracking-wider">Definition</label>
      <textarea
        placeholder="Describe this data element..."
        rows="3"
        [value]="definition()"
        (input)="definition.set($any($event.target).value)"
        class="w-full"
      ></textarea>
    </div>

    <!-- List Items -->
    @if (showListSection()) {
      <div class="flex flex-col gap-1.5 mb-5">
        <div class="flex items-baseline justify-between gap-2">
          <label class="text-xs font-semibold text-navy-600 tracking-wider">List Items</label>
          @if (listItems().length > 1) {
            <span class="text-[11px] text-slate-400">Shown in this order · drag to reorder</span>
          }
        </div>
        <div class="flex items-center gap-2">
          <input
            #newItemInput
            [type]="listInputType()"
            placeholder="Add item..."
            (keydown)="onItemKeydown($event)"
            class="flex-1"
          />
          <button
            type="button"
            (click)="addItem(newItemInput)"
            class="w-9 h-9 flex items-center justify-center rounded-xl bg-accent-500 hover:opacity-90 text-white text-lg font-semibold cursor-pointer transition-opacity shrink-0"
          >
            +
          </button>
        </div>

        @if (listItems().length > 0) {
          <div class="flex flex-wrap gap-2 mt-1">
            @for (item of listItems(); track item; let idx = $index) {
              <div
                draggable="true"
                (dragstart)="onDragStart(idx)"
                (dragover)="onDragOver($event, idx)"
                (drop)="onDrop(idx)"
                (dragend)="onDragEnd()"
                class="inline-flex items-center gap-1.5 rounded-full text-xs font-medium transition-all group cursor-grab"
                [ngClass]="
                  editingIndex() === idx
                    ? 'px-2 py-0.5 bg-white border-2 border-accent-400 ring-2 ring-accent-100'
                    : dragOverIndex() === idx && dragIndex !== idx
                      ? 'px-3 py-1 bg-accent-100 border-2 border-accent-400 text-accent-700'
                      : 'px-3 py-1 bg-slate-100 border-2 border-transparent text-navy-900 hover:border-slate-300'
                "
              >
                @if (editingIndex() === idx) {
                  <input
                    type="text"
                    [value]="editingValue()"
                    (input)="editingValue.set($any($event.target).value)"
                    (keydown.enter)="confirmEdit(idx)"
                    (keydown.escape)="cancelEdit()"
                    (click)="$event.stopPropagation()"
                    class="text-xs font-medium bg-transparent border-0 h-8 outline-none p-0 min-w-12"
                    [style.width]="(editingValue().length || 4) + 2 + 'ch'"
                  />
                  <button
                    type="button"
                    (click)="confirmEdit(idx)"
                    title="Save"
                    class="text-green-600 hover:text-green-700 transition-colors cursor-pointer shrink-0"
                  >
                    <img src="/icons/check-icon.svg" class="w-3.5 h-3.5" alt="Confirm" />
                  </button>
                  <button
                    type="button"
                    (click)="cancelEdit()"
                    title="Cancel"
                    class="text-slate-400 hover:text-slate-600 transition-colors cursor-pointer shrink-0"
                  >
                    <img src="/icons/x-icon.svg" class="w-3 h-3" alt="Cancel" />
                  </button>
                } @else {
                  <!-- Position number makes the display order explicit (JCCA-174) -->
                  <span class="text-xs font-semibold text-slate-400 tabular-nums"
                    >{{ idx + 1 }}.</span
                  >
                  <span class="text-xs font-medium text-navy-900">{{ item.value }}</span>
                  <!-- Always visible (JCCA-146): edit and remove were easy to miss -->
                  <span class="inline-flex items-center -mr-1.5">
                    <button
                      type="button"
                      (click)="startEdit(idx, item.value)"
                      title="Rename"
                      aria-label="Rename item"
                      class="w-6 h-6 flex items-center justify-center rounded-full hover:bg-slate-200 transition-colors cursor-pointer shrink-0"
                    >
                      <img src="/icons/edit-icon.svg" class="w-4 h-4" alt="" />
                    </button>
                    <button
                      type="button"
                      (click)="removeItem(item)"
                      title="Remove"
                      aria-label="Remove item"
                      class="w-6 h-6 flex items-center justify-center rounded-full hover:bg-red-50 transition-colors cursor-pointer shrink-0"
                    >
                      <img src="/icons/trash-red-icon.svg" class="w-4 h-4" alt="" />
                    </button>
                  </span>
                }
              </div>
            }
          </div>
        }
      </div>
    }

    <!-- Assignments: one tab per program type, plus Categories (JCCA-159) -->
    <div class="mb-5">
      <p class="text-xs font-bold text-navy-900 mb-3">
        {{ categoriesTab() ? 'Assignment to Categories' : 'Assignment to Programs' }}
      </p>

      <!-- Program type toggle -->
      @if (programTypes().length > 0 || categoriesSupported()) {
        <div class="flex items-center bg-slate-100 rounded-3xl px-1 py-1 gap-1 mb-5">
          @for (type of programTypes(); track type.id) {
            <button
              type="button"
              (click)="categoriesTab.set(false); selectProgramType(type.id)"
              class="flex-1 px-4 py-2 rounded-full text-xs font-medium transition-all cursor-pointer"
              [ngClass]="
                !categoriesTab() && programTypeFilter() === type.id
                  ? 'bg-white text-navy-900 shadow-sm'
                  : 'text-slate-500 hover:text-navy-700'
              "
            >
              {{ type.name }}
            </button>
          }
          @if (categoriesSupported()) {
            <button
              type="button"
              (click)="categoriesTab.set(true)"
              class="flex-1 px-4 py-2 rounded-full text-xs font-medium transition-all cursor-pointer"
              [ngClass]="
                categoriesTab()
                  ? 'bg-white text-navy-900 shadow-sm'
                  : 'text-slate-500 hover:text-navy-700'
              "
            >
              Categories
            </button>
          }
        </div>
      }

      @if (!categoriesTab()) {
        <div class="flex flex-col gap-1.5">
          @for (entry of filteredEntries(); track entry.id ?? entry.programId; let i = $index) {
            <div
              class="flex flex-col px-3 pt-2.5 pb-2.5 rounded-xl border border-surface-300 bg-white group transition-colors"
            >
              <!-- Row 1: nombre + date range (wraps on mobile) -->
              <div class="flex flex-wrap items-center gap-x-2 gap-y-1.5">
                <span class="text-xs text-slate-400 w-5 shrink-0 text-right">{{ i + 1 }}.</span>
                <div
                  class="w-7 h-7 flex items-center justify-center rounded-lg bg-surface-100 shrink-0"
                >
                  <img src="/icons/folder-icon.svg" class="w-4 h-4" alt="" />
                </div>
                <span class="flex-1 min-w-0 text-xs text-navy-900 truncate">{{
                  programName(entry.programId)
                }}</span>
                <div class="w-full md:w-auto flex items-center gap-3 md:pl-0">
                  <app-date-range-picker
                    variant="compact"
                    [dateFrom]="entry.beginDate ?? ''"
                    [dateTo]="entry.endDate ?? ''"
                    [error]="showErrors() && !entry.beginDate"
                    (rangeChange)="updateProgramDates(entry, $event)"
                  />
                  <button
                    type="button"
                    (click)="removeProgramEntry(entry)"
                    class="w-5 h-5 flex items-center justify-center rounded-full hover:bg-surface-200 transition-colors cursor-pointer shrink-0 opacity-0 group-hover:opacity-100"
                  >
                    <img src="/icons/close-icon.svg" class="w-3 h-3" alt="Remove" />
                  </button>
                </div>
              </div>

              <!-- Row 2: Level + PI Type (recording only) + Required (always, right-aligned) -->
              <div
                class="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 mt-1.5 pl-9 pr-8"
              >
                @if (selectedTypeIsRecording()) {
                  <!-- Level -->
                  <div class="flex items-center gap-1.5">
                    <span class="text-xs text-slate-400">Level</span>
                    <div class="relative shrink-0 flex items-center">
                      <button
                        type="button"
                        (click)="
                          openLevelEntryProgramId.set(
                            openLevelEntryProgramId() === entry.programId ? null : entry.programId
                          )
                        "
                        (blur)="closeLevelEntryDropdown()"
                        class="inline-flex items-center gap-1.5 bg-transparent border-0 p-0 text-xs text-slate-500 hover:text-navy-900 transition-colors cursor-pointer"
                      >
                        {{ (entry.levelIncidentPI ?? 0) === 0 ? 'Incident' : 'PI' }}
                        <img
                          src="/icons/chevron-down-icon.svg"
                          class="w-3 h-3 shrink-0 transition-transform"
                          [ngClass]="
                            openLevelEntryProgramId() === entry.programId ? 'rotate-180' : ''
                          "
                          alt=""
                        />
                      </button>
                      @if (openLevelEntryProgramId() === entry.programId) {
                        <div
                          class="absolute left-0 top-full mt-1 bg-white rounded-xl border border-surface-300 shadow-lg overflow-hidden z-200 py-1 min-w-24"
                        >
                          <button
                            type="button"
                            (mousedown)="
                              updateEntryLevel(entry, 0); openLevelEntryProgramId.set(null)
                            "
                            class="w-full text-left px-4 py-2 text-xs text-navy-900 hover:bg-surface-100 cursor-pointer"
                          >
                            Incident
                          </button>
                          <button
                            type="button"
                            (mousedown)="
                              updateEntryLevel(entry, 1); openLevelEntryProgramId.set(null)
                            "
                            class="w-full text-left px-4 py-2 text-xs text-navy-900 hover:bg-surface-100 cursor-pointer"
                          >
                            PI
                          </button>
                        </div>
                      }
                    </div>
                  </div>
                  <!-- PI Type -->
                  @if ((entry.levelIncidentPI ?? 0) === 1) {
                    <div class="flex items-center gap-1.5">
                      <span class="text-xs text-slate-400">PI Type</span>
                      <div class="relative shrink-0 flex items-center">
                        <button
                          type="button"
                          (click)="
                            openPITypeEntryProgramId.set(
                              openPITypeEntryProgramId() === entry.programId
                                ? null
                                : entry.programId
                            )
                          "
                          (blur)="closePITypeEntryDropdown()"
                          class="inline-flex items-center gap-1.5 bg-transparent border-0 p-0 text-xs text-slate-500 hover:text-navy-900 transition-colors cursor-pointer"
                        >
                          {{ piTypeLabelFor(entry.piInvolvementTypeId) }}
                          <img
                            src="/icons/chevron-down-icon.svg"
                            class="w-3 h-3 shrink-0 transition-transform"
                            [ngClass]="
                              openPITypeEntryProgramId() === entry.programId ? 'rotate-180' : ''
                            "
                            alt=""
                          />
                        </button>
                        @if (openPITypeEntryProgramId() === entry.programId) {
                          <div
                            class="absolute right-0 top-full mt-1 bg-white rounded-xl border border-surface-300 shadow-lg overflow-hidden z-200 py-1 min-w-32"
                          >
                            <button
                              type="button"
                              (mousedown)="
                                updateEntryPIType(entry, null); openPITypeEntryProgramId.set(null)
                              "
                              class="w-full text-left px-4 py-2 text-xs text-navy-900 hover:bg-surface-100 cursor-pointer"
                            >
                              All
                            </button>
                            @for (opt of personTypeOptions(); track opt.value) {
                              <button
                                type="button"
                                (mousedown)="
                                  updateEntryPIType(entry, opt.value);
                                  openPITypeEntryProgramId.set(null)
                                "
                                class="w-full text-left px-4 py-2 text-xs text-navy-900 hover:bg-surface-100 cursor-pointer"
                              >
                                {{ opt.label }}
                              </button>
                            }
                          </div>
                        }
                      </div>
                    </div>
                  }
                }
                <!-- Required — always shown, pushed to the right on desktop -->
                <label class="flex items-center gap-1.5 cursor-pointer select-none md:ml-auto">
                  <input
                    type="checkbox"
                    [checked]="!!entry.requiredOptional"
                    (change)="updateEntryRequired(entry, $any($event.target).checked)"
                    class="w-3.5 h-3.5 rounded cursor-pointer accent-accent-500"
                  />
                  <span class="text-xs text-slate-500">Required</span>
                </label>
              </div>
            </div>
          }

          <!-- Add program: same searchable select as the Categories tab -->
          @if (loadingPrograms()) {
            <span class="text-xs text-slate-400 px-3 py-2.5">Loading programs...</span>
          } @else {
            <app-select
              placeholder="Add program..."
              [options]="availablePrograms()"
              [searchable]="true"
              value=""
              (changed)="addProgramEntry($event)"
            />
          }
        </div>
      } @else {
        <p class="text-2xs text-slate-400 mb-3">
          Shown in Additional Data when an incident has one of these situation categories.
        </p>
        <div class="flex flex-col gap-2">
          @for (link of categoryLinks(); track link.situationTaxonomyMemberId) {
            <div
              class="flex items-center justify-between px-3 py-2.5 rounded-xl border border-surface-300 bg-white text-xs text-navy-900"
            >
              <span class="min-w-0 truncate">{{ categoryLabel(link) }}</span>
              <label class="flex items-center gap-1.5 cursor-pointer select-none ml-auto shrink-0">
                <input
                  type="checkbox"
                  [checked]="link.requiredOptional === 1"
                  (change)="
                    setCategoryRequired(link.situationTaxonomyMemberId, $any($event.target).checked)
                  "
                  class="w-3.5 h-3.5 rounded cursor-pointer accent-accent-500"
                />
                <span class="text-xs text-slate-500">Required</span>
              </label>
              <button
                type="button"
                (click)="removeCategory(link.situationTaxonomyMemberId)"
                title="Remove"
                aria-label="Remove category"
                class="text-slate-400 hover:text-red-500 transition-colors cursor-pointer ml-2 shrink-0"
              >
                <img src="/icons/x-icon.svg" class="w-3.5 h-3.5" alt="" />
              </button>
            </div>
          }

          @if (loadingCategories()) {
            <span class="text-xs text-slate-400 px-3 py-2.5">Loading categories...</span>
          } @else {
            <app-select
              placeholder="Add category..."
              [options]="availableCategories()"
              [searchable]="true"
              value=""
              (changed)="addCategory($event)"
            />
          }
        </div>
      }
    </div>
  }

  <div modal-footer class="flex items-center justify-between gap-3">
    @if (confirmRemove()) {
      <div class="flex items-center gap-2">
        <span class="text-xs text-slate-500">Delete this element?</span>
        <button
          type="button"
          (click)="onRemove()"
          [disabled]="removing()"
          class="px-3 py-1.5 text-xs font-semibold text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {{ removing() ? 'Deleting...' : 'Confirm' }}
        </button>
        <button
          type="button"
          (click)="confirmRemove.set(false)"
          [disabled]="removing()"
          class="px-3 py-1.5 text-xs font-semibold text-slate-500 hover:text-navy-900 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
      </div>
    } @else {
      <button
        type="button"
        (click)="onRemove()"
        class="flex items-center gap-1.5 text-xs font-semibold text-red-500 hover:text-red-600 transition-colors cursor-pointer"
      >
        <img src="/icons/trash-icon.svg" class="w-4 h-4" alt="" />
        Delete data element
      </button>
    }

    <div class="flex items-center gap-3">
      <button
        type="button"
        (click)="closed.emit()"
        class="px-6 py-2.5 text-sm font-semibold text-slate-500 hover:text-navy-900 transition-colors cursor-pointer"
      >
        Cancel
      </button>
      <button
        type="button"
        (click)="onSubmit()"
        [disabled]="submitting() || loading() || !hasChanges()"
        class="px-6 py-2.5 text-sm font-semibold text-white bg-accent-500 hover:opacity-90 rounded-xl transition-opacity cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {{ submitting() ? 'Saving...' : 'Save Changes' }}
      </button>
    </div>
  </div>
</app-modal>
